
// Copyright (c) Docugami, Inc. All rights reserved.
import { IconDescriptor } from "components/work_path/IconRenderer";
import { LandingPageCoachMark } from "components/work_path/LandingPageCoachMark";
import { WorkPathCategory, getIconInfoForWorkPathCategory } from "components/work_path/WorkPathsHelper";
import { useFileCount } from "filemanager/FileCountView";
import { WorkPathType } from "networking/WorkPathApi";
import React from "react";
import { selectIsLimitedOrganization } from "store/AccountInfoHelpers";
import { useNavigationService, NavigationType } from "./navigationService";
import { usePageLimits } from "./PageLimits";
import { useAppSelector } from "./redux";

export function useProjectsCoachMark(category?: WorkPathCategory) {
    const isEmpty = useFileCount() === 0;
    const { pageUploadDisabled } = usePageLimits();
    const createProjectsDisabled = useAppSelector((state) => selectIsLimitedOrganization(state));
    const navService = useNavigationService();

    let descriptionText = '';
    let iconSources: IconDescriptor[] = [];
    const link = {text: '', onClick: () => {}};

    switch (category) {
        case WorkPathCategory.Understand:
            descriptionText = "As you generate reports they will appear here."
            link.text = "generating a Tabular report"
            link.onClick = () => navService.navToNewWorkPathPage(NavigationType.PushNewPage, WorkPathType.TabularReport);
            iconSources = [getIconInfoForWorkPathCategory(WorkPathCategory.Understand)];
            break;
        case WorkPathCategory.Create:
            descriptionText = "As you author docs they will appear here."
            link.text = "generating an Abstract"
            link.onClick = () => navService.navToNewWorkPathPage(NavigationType.PushNewPage, WorkPathType.Abstract);
            iconSources = [getIconInfoForWorkPathCategory(WorkPathCategory.Create)];
            break;
        case WorkPathCategory.Workflow:
            descriptionText = "As you generate workflows they will appear here."
            link.text = "generating a Zapier workflow"
            link.onClick = () => navService.navToNewWorkPathPage(NavigationType.PushNewPage, WorkPathType.ZapierWorkFlow);
            iconSources = [getIconInfoForWorkPathCategory(WorkPathCategory.Workflow)];
            break;
        default:
            descriptionText = "As you work, your reports and more will appear here."
            link.text = "generating a Tabular report"
            link.onClick = () => navService.navToNewWorkPathPage(NavigationType.PushNewPage, WorkPathType.TabularReport);
            iconSources = Object.values(WorkPathCategory).map(c => getIconInfoForWorkPathCategory(c as WorkPathCategory));
    }

    // Reset link text and action if the user has no files, or limits are enforced
    if (isEmpty) {
        if (pageUploadDisabled) {
            link.text = "upgrading to upload some documents."
            link.onClick = () => navService.navToAccountPage(NavigationType.PushNewPage);
        } else {
            link.text = "uploading some documents."
            link.onClick = navService.navToAddDocsPage;
        }
    } else if (createProjectsDisabled) {
        // Set link text for each category if creating projects is disabled
        switch (category) {
            case WorkPathCategory.Understand:
                link.text = "upgrading to generate reports.";
                break;
            case WorkPathCategory.Create:
                link.text = "upgrading to author documents.";
                break;
            case WorkPathCategory.Workflow:
                link.text = "upgrading to generate workflows.";
                break;
            default:
                link.text = "upgrading to generate projects.";
        }
        link.onClick = () => navService.navToAccountPage(NavigationType.PushNewPage);
    }

    return <LandingPageCoachMark iconSources={iconSources} descriptionText={descriptionText} link={link} />
}